import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { Play, Star, Volume2, VolumeX } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { parseDriveFileId, toEmbedUrl, toPosterUrl } from '../lib/video';
import {
  isPlaybackUnlocked,
  onPlaybackUnlock,
  unlockPlayback,
  watchForFirstTap,
} from '../lib/playbackUnlock';
import {
  playerFrameStyle,
  playerStageStyle,
  playerVideoStyle,
  usePlayerScale,
} from '../hooks/usePlayerScale';

/**
 * How close a review's middle has to come to the middle of the screen before it
 * counts as centred at all, as a fraction of the viewport height.
 *
 * Nothing plays when no review is this near the middle — which is what stops a
 * review carrying on out loud over the checkout section further down the page.
 *
 * ⚠ It has to stay comfortably more than *half* the distance between two cards,
 *   or there is a gap between them where one has left the middle and the next
 *   has not arrived and the reel goes silent mid-scroll. At 0.3 of an 844px
 *   screen — less the sticky header, so about 756px — that is 227px against a
 *   card pitch of about 180px.
 */
const CENTRE_REACH = 0.3;

/**
 * How much nearer the middle a review has to be than the one already playing
 * before it takes over, in px.
 *
 * Purely anti-flap. Without it the two cards either side of the middle trade
 * the reel back and forth over a pixel of scrolling, and every trade restarts a
 * video. It deliberately does *not* work like a hold band: an earlier version
 * kept the playing review while it was anywhere within CENTRE_REACH of the
 * middle, which is wider than a whole card — so scrolling the next review to
 * dead centre left the previous one still talking.
 */
const SWITCH_MARGIN = 24;
/** How long the scroll position is ignored after one review hands to the next,
 *  so the handoff is not overruled before its scroll has landed. */
const HANDOFF_LOCK_MS = 1100;
/** How far outside the viewport a review is worth a poster frame. */
const NEAR_MARGIN = '600px 0px';

/** Movement under this is a tap rather than a drag, in px. Also the distance a
 *  touch has to travel before the scrubber decides whether the visitor meant to
 *  scrub it or to scroll the page past it. */
const SCRUB_SLOP = 6;

/** Re-renders every player the moment the visitor has asked for sound. */
function useUnlocked() {
  return useSyncExternalStore(onPlaybackUnlock, isPlaybackUnlocked, () => false);
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * The middle of the part of the screen the visitor can actually see, and how
 * much of it counts as "the middle".
 *
 * Not `innerHeight / 2`: the header is sticky, and the page already declares
 * how much of the top it covers as `scroll-padding-top` on the root. That
 * declaration is not optional to honour — `scrollIntoView({block: 'center'})`
 * obeys it too, so a rule here that measured against the raw viewport would
 * disagree with the reel's own handoff by exactly half the header, and every
 * review would come to rest off the mark it is judged against. Read from the
 * stylesheet rather than copied, so the two cannot drift apart.
 */
function centreOfView() {
  const inset =
    parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
  const height = window.innerHeight - inset;
  return { middle: inset + height / 2, reach: height * CENTRE_REACH };
}

/**
 * Which player a review needs.
 *
 * Every review on the site today is an mp4 in Firebase Storage, which is the
 * case worth optimising for: a real `<video>` is ours to command — it can be
 * preloaded, muted, paused and, the part this section is built on, it says so
 * when it reaches the end. The embed branches are kept for a link pasted into
 * the admin console, and they degrade in the obvious way: YouTube reports its
 * own ending over the frame API, Drive reports nothing at all and so simply
 * does not hand on to the review below it.
 */
function playerFor(url) {
  const embed = toEmbedUrl(url);
  if (parseDriveFileId(url)) return { kind: 'drive', embed, direct: null };
  if (!embed) return { kind: 'file', embed: null, direct: url };
  if (embed.includes('youtube.com')) return { kind: 'youtube', embed, direct: null };
  if (embed.includes('vimeo.com')) return { kind: 'vimeo', embed, direct: null };
  return { kind: 'drive', embed, direct: null };
}

/** Autoplaying embed address. Muted playback is the only kind allowed to start
 *  on its own, and for YouTube the API has to be switched on to hear the end. */
function embedSrc(kind, embed, muted) {
  const join = embed.includes('?') ? '&' : '?';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const originParam = origin ? `&origin=${encodeURIComponent(origin)}` : '';
  if (kind === 'youtube') {
    return `${embed}${join}autoplay=1&mute=${muted ? 1 : 0}&playsinline=1&rel=0&modestbranding=1&enablejsapi=1${originParam}`;
  }
  if (kind === 'vimeo') {
    return `${embed}${join}autoplay=1&muted=${muted ? 1 : 0}&playsinline=1`;
  }
  return `${embed}${join}autoplay=1`;
}

/**
 * The embed slot, mounted only while its review is the one playing.
 *
 * Cross-origin players have no API to pause, so mounting and unmounting the
 * frame is the only way to start and stop one.
 */
function StoryEmbed({ story, muted, onEnded }) {
  const { kind, embed } = story.player;
  const [frameBoxRef, scale] = usePlayerScale();
  const frameRef = useRef(null);
  const src = useMemo(() => embedSrc(kind, embed, muted), [kind, embed, muted]);

  /* YouTube only talks to a page that has said hello first, and it reports the
     end twice over — as `onStateChange` and inside the `infoDelivery` firehose
     — so both shapes are read and state 0 is the one that matters. */
  useEffect(() => {
    if (kind !== 'youtube') return undefined;

    const onMessage = (event) => {
      if (event.source !== frameRef.current?.contentWindow) return;
      let host;
      try {
        host = new URL(event.origin).hostname;
      } catch {
        return;
      }
      if (!/(^|\.)youtube(-nocookie)?\.com$/.test(host)) return;
      let data;
      try {
        data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }
      if (data?.info?.playerState === 0 || data?.info === 0) onEnded();
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [kind, onEnded]);

  return (
    <span ref={frameBoxRef} className="absolute inset-0 z-10 block">
      <span className="player-stage" style={playerStageStyle(scale)}>
        <iframe
          ref={frameRef}
          src={src}
          title={`${story.name} review`}
          onLoad={() => {
            frameRef.current?.contentWindow?.postMessage(
              JSON.stringify({ event: 'listening' }),
              '*'
            );
          }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
          allowFullScreen
          className="absolute inset-0 w-full h-full border-0 bg-black"
        />
      </span>
    </span>
  );
}

/**
 * Every student review the workshop has, stacked down one section and playing
 * one after another.
 *
 * These used to sit one per day at the head of the curriculum cards, which
 * meant a visitor only ever met the review belonging to whatever day they had
 * scrolled level with, and no player could hand on to another. Gathered here
 * they read as what they are — a reel. The first starts by itself when it is
 * reached, and each one that runs to its end starts the review below it and
 * brings it up the screen.
 *
 * ⚠ Exactly one review may be talking at a time. That is why the section owns
 *   playback rather than each card deciding for itself, and it is the failure
 *   to watch for in anything changed here.
 *
 * Two things decide what plays, in this order:
 *   · a review that has just finished hands to the next one, and for a moment
 *     afterwards nothing may overrule it — see HANDOFF_LOCK_MS;
 *   · otherwise it is simply whichever review the visitor is level with, with
 *     the hysteresis above so that scrolling between two does not churn them.
 *
 * A review that has been watched to the end is not started again by scrolling
 * past it — without that, the last one in the list restarts itself forever once
 * the reel reaches the bottom. A tap plays it regardless.
 */
export const SuccessStories = () => {
  const { testimonials } = useApp();

  const stories = useMemo(
    () =>
      testimonials
        .filter((item) => item.published !== false && item.videoUrl)
        .map((item) => ({
          id: item.id,
          name: item.studentName || 'Student review',
          stars: Math.min(5, Math.max(1, Number(item.rating) || 5)),
          poster: item.posterUrl || toPosterUrl(item.videoUrl),
          player: playerFor(item.videoUrl),
        })),
    [testimonials]
  );

  const count = stories.length;

  const [activeId, setActiveId] = useState(null);
  /** Paused by the visitor. */
  const [held, setHeld] = useState(false);
  /** A tab that has been switched away from is not on screen either, and the
   *  scroll position has no reason to say so — nothing moved. */
  const [hidden, setHidden] = useState(
    () => typeof document !== 'undefined' && document.hidden
  );
  /** Reviews close enough to the viewport to be worth a poster frame. */
  const [near, setNear] = useState(() => new Set());
  /** Whether the first review may start fetching yet — see the effect below. */
  const [warm, setWarm] = useState(false);
  /** Bumped when the watched-to-the-end set changes, which lives in a ref
   *  because measure() is made once and never sees new state. */
  const [, bumpFinished] = useState(0);

  /**
   * Sound is on by default, and this is the whole of what "by default" can mean.
   *
   * A page may not start audible media until the browser has seen a real
   * gesture from the visitor, and nothing on our side overrides that — it is
   * enforced above us. So the reel does not *assume* sound, it *asks* for it
   * and records what it got.
   *
   * Two pieces, and they are not the same thing:
   *   · `soundOff` is what the visitor asked for. Theirs, and it outranks
   *     everything.
   *   · `audible` is what the browser actually granted. Set from the outcome of
   *     `play()` rather than from what we requested, so the speaker icon and
   *     the invitation are never claiming sound the visitor cannot hear.
   *
   * There is deliberately no "refused" latch. Every review asks again as it
   * starts, so the first one the browser allows is audible with nothing
   * pressed — which on a phone is usually the very first one, because the
   * scroll that carries the visitor down to the reel is itself a touch gesture
   * and grants the permission on the way. That is also why the watcher below
   * runs from page load rather than from the first refusal: waiting for a
   * refusal to start listening is waiting until after the gesture has gone by.
   */
  const unlocked = useUnlocked();
  const [soundOff, setSoundOff] = useState(false);
  const [audible, setAudible] = useState(false);
  /** What the visitor can actually hear, which is what the controls must show. */
  const muted = !audible;

  const listRef = useRef(null);
  /** id → the card's box, the video inside it, and its progress bar. */
  const boxes = useRef(new Map());
  const videos = useRef(new Map());
  const bars = useRef(new Map());
  const knobs = useRef(new Map());
  /** The review currently under a dragging finger, if any. While this is set
   *  the drag owns the bar and `timeupdate` must keep its hands off it. */
  const scrubbing = useRef(null);

  const activeRef = useRef(null);
  const finished = useRef(new Set());
  const nearRef = useRef(new Set());
  const lockUntil = useRef(0);
  const frame = useRef(0);

  const setActive = useCallback((id) => {
    if (activeRef.current === id) return;
    activeRef.current = id;
    setActiveId(id);
    setHeld(false);
  }, []);

  /* ── Which review is in the middle of the screen ────────────────────────── */
  const measure = useCallback(() => {
    if (!listRef.current) return;
    // A handoff has just chosen the review below and is scrolling it up the
    // screen. Until that lands the scroll position still describes the one that
    // finished, and reading it would take the handoff straight back.
    if (Date.now() < lockUntil.current) return;

    const { middle, reach } = centreOfView();
    const offsets = new Map();
    for (const [id, node] of boxes.current) {
      const box = node.getBoundingClientRect();
      if (box.height === 0) continue;
      offsets.set(id, Math.abs(box.top + box.height / 2 - middle));
    }
    if (offsets.size === 0) return;

    /* The review nearest the middle of the screen, so long as one is near it at
       all. Ties go to the earlier one, which is why this walks the list in
       order rather than the ref map — several cards share a phone screen and
       the ref map's order is whatever React happened to mount in. */
    let best = null;
    let bestOffset = Infinity;
    for (const story of stories) {
      if (finished.current.has(story.id)) continue;
      const offset = offsets.get(story.id);
      if (offset === undefined || offset > reach) continue;
      if (offset < bestOffset) {
        best = story.id;
        bestOffset = offset;
      }
    }

    /* Hand over only once the newcomer is clearly nearer — see SWITCH_MARGIN.
       Note this is not a reason to keep a review that has itself left the
       middle: past the bottom of the reel `best` is null and the one that was
       playing has to stop, or it talks over the checkout section below. */
    const current = activeRef.current;
    if (current && best !== current) {
      const currentOffset = offsets.get(current) ?? Infinity;
      if (currentOffset <= reach && bestOffset > currentOffset - SWITCH_MARGIN) return;
    }

    setActive(best);
  }, [stories, setActive]);

  useEffect(() => {
    // Scroll fires far more often than the screen repaints; coalesce to a frame.
    const onScroll = () => {
      cancelAnimationFrame(frame.current);
      frame.current = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measure);

    // Layout changes go straight to measure(): the first reading necessarily
    // runs before the posters and web fonts have settled, and each of those
    // moves the cards.
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => measure());
    if (observer && listRef.current) observer.observe(listRef.current);

    return () => {
      cancelAnimationFrame(frame.current);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [measure]);

  /* ── Which reviews are close enough to be worth a picture ───────────────── */
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      const all = new Set(stories.map((story) => story.id));
      nearRef.current = all;
      setNear(all);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const next = new Set(nearRef.current);
        let changed = false;
        for (const entry of entries) {
          const id = entry.target.dataset.storyId;
          if (entry.isIntersecting && !next.has(id)) {
            next.add(id);
            changed = true;
          }
        }
        if (!changed) return;
        nearRef.current = next;
        setNear(next);
      },
      // Deliberately generous: a poster that starts loading as the card arrives
      // is a black box for as long as the fetch takes. This gets it a screen
      // and a half early, and still costs a visitor who never scrolls this far
      // nothing at all.
      { rootMargin: NEAR_MARGIN }
    );

    for (const node of boxes.current.values()) observer.observe(node);
    return () => observer.disconnect();
  }, [stories]);

  /* ── Tab switching ──────────────────────────────────────────────────────── */
  useEffect(() => {
    const onVisibility = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  /**
   * When the first review may start fetching.
   *
   * It sits well over a screen down a landing page, so there is no version of
   * "when it is needed" that is soon enough — a visitor who scrolls to it
   * should find it already buffered rather than watch it spool up. But it must
   * not be fetched *instead of* the page: several megabytes of mp4 racing the
   * hero for the same connection is how a fast page becomes a slow one on the
   * mobile traffic this is bought for. So the fetch is queued for the first
   * idle moment after the first paint, which on any real connection is many
   * seconds before anybody has scrolled that far.
   */
  useEffect(() => {
    const start = () => setWarm(true);
    if (typeof requestIdleCallback === 'function') {
      const handle = requestIdleCallback(start, { timeout: 2500 });
      return () => cancelIdleCallback(handle);
    }
    const timer = setTimeout(start, 1500);
    return () => clearTimeout(timer);
  }, []);

  /* ── One review plays; the rest are stopped ─────────────────────────────── */
  const playing = Boolean(activeId) && !held && !hidden;

  useEffect(() => {
    for (const [id, node] of videos.current) {
      if (id !== activeId && !node.paused) node.pause();
    }

    const node = activeId ? videos.current.get(activeId) : null;
    if (!node) return;

    if (!playing) {
      node.pause();
      return;
    }

    // Rewound on the way back in rather than on the way out, so a review that
    // has handed on keeps its last frame on screen while it scrolls away.
    if (node.ended) node.currentTime = 0;

    /* Ask for sound whenever asking is free.
       Starting a stopped review unmuted costs nothing if it is refused: the
       promise rejects, the video is still sitting where it was, and the muted
       retry below gets it going. Unmuting one that is *already running* is a
       different matter — without a gesture the browser does not refuse that
       request, it pauses the video — so that is only done once `unlocked` says
       a gesture has happened. */
    const askForSound = !soundOff && (node.paused || unlocked);
    const wantMuted = !askForSound;

    /* Say nothing to a player that is already doing the right thing.
       This effect re-runs on anything that changes what should be playing, and
       scrolling changes that constantly — but `muted` and `play()` are not free
       to restate. Assigning `muted` fires `volumechange` and, on a running
       element, can make the browser re-evaluate whether it is allowed to be
       playing at all; traced in headless Chrome, the first scroll after a
       review started produced volumechange → pause → volumechange → play at one
       and the same timestamp. Harmless-looking, and a stutter in the audio. */
    if (node.muted !== wantMuted) node.muted = wantMuted;
    if (!node.paused) {
      setAudible(!node.muted);
      return undefined;
    }

    let cancelled = false;
    node
      .play()
      .then(() => {
        if (!cancelled) setAudible(!node.muted);
      })
      .catch(() => {
        // Refused out loud. Muted playback needs no permission, so fall back to
        // it rather than leave the review sitting on its poster — it plays, it
        // just cannot be heard yet, and the invitation says so.
        if (cancelled) return;
        setAudible(false);
        node.muted = true;
        node.play().catch(() => {});
      });

    return () => {
      cancelled = true;
    };
  }, [activeId, playing, soundOff, unlocked]);

  /* Watch for the visitor's first real gesture from page load — not from the
     first time a review is refused sound, which is too late to catch the one
     gesture that matters most. */
  useEffect(() => {
    if (unlocked) return undefined;
    return watchForFirstTap(null);
  }, [unlocked]);

  /* ── Hand on to the review below ────────────────────────────────────────── */
  const onEnded = useCallback(
    (id) => {
      finished.current.add(id);
      bumpFinished((n) => n + 1);

      const i = stories.findIndex((story) => story.id === id);
      const next = i >= 0 ? stories[i + 1] : null;
      // The last one simply stops. Handing back to the top would scroll the
      // visitor up the page against their will, and leaving it to the scroll
      // position would restart the review that just finished, forever.
      if (!next) {
        setActive(null);
        return;
      }

      lockUntil.current = Date.now() + HANDOFF_LOCK_MS;
      setActive(next.id);
      boxes.current.get(next.id)?.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'center',
      });
    },
    [stories, setActive]
  );

  /* ── The progress line, written straight onto the nodes ─────────────────── */

  /** Four repaints a second, and none of them a React render. */
  const paint = useCallback((id, fraction) => {
    const bar = bars.current.get(id);
    if (bar) bar.style.transform = `scaleX(${fraction})`;
    const knob = knobs.current.get(id);
    if (knob) {
      knob.style.left = `${fraction * 100}%`;
      knob.parentElement?.parentElement?.setAttribute(
        'aria-valuenow',
        String(Math.round(fraction * 100))
      );
    }
  }, []);

  const onTimeUpdate = useCallback(
    (event) => {
      const node = event.currentTarget;
      const id = node.dataset.storyId;
      // A drag is under way on this bar; it says where the bar goes, not the
      // clock. A press that has not yet declared itself does not count — the
      // bar must keep moving while the gesture is still ambiguous.
      if (scrubbing.current?.id === id && scrubbing.current.active) return;
      if (!node.duration) return;
      paint(id, Math.min(1, node.currentTime / node.duration));
    },
    [paint]
  );

  /* ── Dragging the bar ───────────────────────────────────────────────────── */

  const seekTo = useCallback(
    (id, clientX, rect) => {
      const node = videos.current.get(id);
      if (!node || !Number.isFinite(node.duration) || !rect.width) return;
      const fraction = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      node.currentTime = fraction * node.duration;
      paint(id, fraction);
    },
    [paint]
  );

  /** Take the review being scrubbed, so the bar under the finger is the bar that
   *  is playing. Un-finishes it too, or the reel would skip straight past a
   *  review the visitor has just deliberately rewound. */
  const claim = useCallback(
    (id) => {
      unlockPlayback();
      if (id !== activeRef.current) {
        finished.current.delete(id);
        bumpFinished((n) => n + 1);
        // The scroll position has not moved and would otherwise put the
        // previous review straight back — same reason a tap sets this.
        lockUntil.current = Date.now() + HANDOFF_LOCK_MS;
        setActive(id);
      }
      setHeld(false);
    },
    [setActive]
  );

  /**
   * Pressing the bar does not seek yet — which way the finger goes decides.
   *
   * ⚠ It used to seek on `pointerdown`, and that was wrong in a way only a
   *   touchscreen shows: the strip runs the full width of the card along its
   *   bottom edge, so a thumb starting an ordinary upward swipe anywhere near
   *   the foot of a card landed on the bar and threw the review to wherever the
   *   thumb happened to be — traced in Chrome, a scroll gesture starting at the
   *   horizontal centre jumped a 109s review to 54.5s. `touch-action: pan-y`
   *   does not save you here: it lets the browser take the vertical pan, but
   *   `pointerdown` has already fired and already done the damage.
   *
   *   So a touch has to declare itself. Move mostly sideways and it is a scrub;
   *   move mostly up or down and it is the page being scrolled, and the bar
   *   keeps out of it. Let go without really moving and it was a tap, which
   *   means seek to that spot. A mouse has no such ambiguity — there is no
   *   scrolling by dragging — so it seeks on press as you would expect.
   */
  const onScrubStart = useCallback(
    (event, id) => {
      const node = videos.current.get(id);
      if (!node) return;

      const strip = event.currentTarget;
      scrubbing.current = {
        id,
        startX: event.clientX,
        startY: event.clientY,
        active: false,
      };

      if (event.pointerType === 'mouse') {
        event.stopPropagation();
        strip.setPointerCapture?.(event.pointerId);
        scrubbing.current.active = true;
        strip.dataset.scrubbing = 'true';
        claim(id);
        seekTo(id, event.clientX, strip.getBoundingClientRect());
      }
    },
    [claim, seekTo]
  );

  const onScrubMove = useCallback(
    (event, id) => {
      const drag = scrubbing.current;
      if (!drag || drag.id !== id) return;
      const strip = event.currentTarget;

      if (!drag.active) {
        const dx = Math.abs(event.clientX - drag.startX);
        const dy = Math.abs(event.clientY - drag.startY);
        // Mostly vertical: the visitor is scrolling the page. Stand down for
        // the rest of this gesture rather than fighting it.
        if (dy > dx && dy > SCRUB_SLOP) {
          scrubbing.current = null;
          return;
        }
        if (dx <= SCRUB_SLOP) return;
        drag.active = true;
        strip.setPointerCapture?.(event.pointerId);
        strip.dataset.scrubbing = 'true';
        claim(id);
      }

      event.stopPropagation();
      // Re-read the box every move rather than caching it at the start: the
      // handoff can scroll the page out from under a finger that is still down.
      seekTo(id, event.clientX, strip.getBoundingClientRect());
    },
    [claim, seekTo]
  );

  const onScrubEnd = useCallback(
    (event, id) => {
      const drag = scrubbing.current;
      if (!drag || drag.id !== id) return;
      const strip = event.currentTarget;

      // Pressed and released without really moving: a tap on the bar, which
      // means seek to where it was tapped.
      if (!drag.active && event.type === 'pointerup') {
        const dx = Math.abs(event.clientX - drag.startX);
        const dy = Math.abs(event.clientY - drag.startY);
        if (dx <= SCRUB_SLOP && dy <= SCRUB_SLOP) {
          event.stopPropagation();
          claim(id);
          seekTo(id, event.clientX, strip.getBoundingClientRect());
        }
      } else if (drag.active) {
        event.stopPropagation();
      }

      strip.releasePointerCapture?.(event.pointerId);
      delete strip.dataset.scrubbing;
      scrubbing.current = null;
    },
    [claim, seekTo]
  );

  /** Arrow keys nudge, Home and End jump. Stopped from bubbling so they do not
   *  also reach the card, where space and enter mean play/pause. */
  const onScrubKey = useCallback(
    (event, id) => {
      const node = videos.current.get(id);
      if (!node || !Number.isFinite(node.duration)) return;

      const step =
        event.key === 'ArrowRight' ? 5 : event.key === 'ArrowLeft' ? -5 : null;
      let next = null;
      if (step !== null) next = node.currentTime + step;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = node.duration;
      if (next === null) return;

      event.preventDefault();
      event.stopPropagation();
      node.currentTime = Math.min(node.duration, Math.max(0, next));
      paint(id, node.currentTime / node.duration);
    },
    [paint]
  );

  /**
   * The tap on a review, which means different things in the order a visitor is
   * most likely to want them.
   *
   * On a review that is not playing it means play this one — and it beats the
   * scroll position, including on one already watched to the end. On the one
   * that is playing it means "let me hear this" while the reel is still silent,
   * which is the single thing autoplay cannot give them, and play/pause once it
   * has sound.
   */
  const onTap = useCallback(
    (id) => {
      unlockPlayback();

      if (id !== activeRef.current) {
        finished.current.delete(id);
        bumpFinished((n) => n + 1);
        lockUntil.current = Date.now() + HANDOFF_LOCK_MS;
        const node = videos.current.get(id);
        if (node?.ended) node.currentTime = 0;
        setActive(id);
        return;
      }

      // Cannot hear it: the tap is asking for sound, not for a pause.
      if (!audible) {
        setSoundOff(false);
        setHeld(false);
        return;
      }

      setHeld((was) => !was);
    },
    [audible, setActive]
  );

  const toggleSound = useCallback(
    (event) => {
      event.stopPropagation();
      unlockPlayback();
      // The button shows what the visitor can *hear*, so it has to act on that
      // rather than on what was last asked for: while nothing is audible it
      // means "sound on", whatever the reason for the silence. Toggling the
      // request instead would turn a review the browser had muted into one the
      // visitor had muted, and the tap would appear to do nothing.
      setSoundOff(audible);
    },
    [audible]
  );

  if (count === 0) return null;

  const activeIndex = stories.findIndex((story) => story.id === activeId);

  return (
    <section id="success-stories" className="section story-section bg-[#fffdfb] text-ink-900">
      <div className="shell">
        <span className="pill pill-gold">Real students</span>
        <h2 className="mt-3.5 font-display text-display-md font-bold">
          Success Stories That{' '}
          <span className="font-display-italic text-brand-700">Speak for Themselves</span>
        </h2>
        <p className="mt-2.5 text-sm text-ink-500 leading-relaxed">
          {count} students on what changed for them, in their own words. They play one
          after another, top to bottom — nothing to press.
        </p>
      </div>

      <ol ref={listRef} className="shell mt-6 space-y-4">
        {stories.map((story, i) => {
          const isActive = story.id === activeId;
          const isPlaying = isActive && playing;
          // The review below the one playing is fetched while it plays, so the
          // handoff is a repaint rather than a wait. Before anything plays that
          // is the first one — and only once the page itself has gone quiet.
          const eager =
            isActive || (activeIndex >= 0 ? i === activeIndex + 1 : i === 0 && warm);
          const showPoster = near.has(story.id) && Boolean(story.poster);

          return (
            <li key={story.id}>
              <div
                ref={(node) => {
                  if (node) boxes.current.set(story.id, node);
                  else boxes.current.delete(story.id);
                }}
                data-story-id={story.id}
                data-playing={isPlaying}
                onClick={() => onTap(story.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onTap(story.id);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={
                  !isActive
                    ? `Play ${story.name}'s review`
                    : muted
                      ? `Play ${story.name}'s review with sound`
                      : held
                        ? `Resume ${story.name}'s review`
                        : `Pause ${story.name}'s review`
                }
                style={playerFrameStyle}
                className="story-card player-frame block w-full rounded-[1.4rem] bg-ink-950 cursor-pointer select-none"
              >
                {story.player.direct ? (
                  <video
                    ref={(node) => {
                      if (node) videos.current.set(story.id, node);
                      else videos.current.delete(story.id);
                    }}
                    data-story-id={story.id}
                    src={story.player.direct}
                    poster={showPoster ? story.poster : undefined}
                    preload={eager ? 'auto' : 'none'}
                    playsInline
                    muted={muted}
                    onEnded={() => onEnded(story.id)}
                    onTimeUpdate={onTimeUpdate}
                    style={playerVideoStyle}
                    className="z-10 bg-black"
                  />
                ) : isPlaying ? (
                  <StoryEmbed story={story} muted={muted} onEnded={() => onEnded(story.id)} />
                ) : (
                  showPoster && (
                    <img
                      src={story.poster}
                      alt=""
                      draggable={false}
                      style={playerVideoStyle}
                      className="z-10"
                    />
                  )
                )}

                {/* Idle: say so, rather than leave a still frame that could as
                    easily be a player that has failed. */}
                {!isPlaying && (
                  <span className="absolute inset-0 z-30 flex items-center justify-center bg-ink-950/35">
                    <span className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center shadow-lg">
                      <Play className="w-5 h-5 ml-0.5 text-brand-700 fill-brand-700" />
                    </span>
                  </span>
                )}

                {/* Video rating stars and sound toggle */}
                <span className="absolute inset-x-0 bottom-0 z-30 flex items-end justify-between gap-3 px-4 pb-3 pt-10 bg-gradient-to-t from-ink-950/90 via-ink-950/35 to-transparent pointer-events-none">
                  <span className="flex items-center gap-0.5" aria-hidden="true">
                    {Array.from({ length: story.stars }).map((_, star) => (
                      <Star key={star} className="w-3.5 h-3.5 fill-gold-400 text-gold-400" />
                    ))}
                  </span>

                  {isActive && (
                    <button
                      type="button"
                      onClick={toggleSound}
                      aria-label={muted ? 'Turn the sound on' : 'Mute'}
                      className="pointer-events-auto shrink-0 w-9 h-9 rounded-full bg-ink-950/70 text-white border border-white/25 backdrop-blur-md flex items-center justify-center transition-colors hover:bg-brand-600"
                    >
                      {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                    </button>
                  )}
                </span>

                {/* The one invitation on the section, and only while it has
                    something to ask for. Autoplay gets the reel running; sound
                    is the single thing it cannot give without being asked. */}
                {isPlaying && !audible && !soundOff && (
                  <span
                    data-tap-invite
                    className="absolute top-2.5 left-3 z-40 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-ink-950/85 text-white border border-white/20 shadow-2xl backdrop-blur-md pointer-events-none"
                  >
                    <span className="w-4 h-4 rounded-full bg-brand-600/90 flex items-center justify-center">
                      <Volume2 className="w-2.5 h-2.5 text-gold-300 animate-pulse" />
                    </span>
                    <span className="font-sans text-[9px] font-bold uppercase tracking-[0.14em]">
                      Tap for sound
                    </span>
                  </span>
                )}

                <span className="absolute top-2.5 right-3 z-30 text-[10px] font-bold tracking-[0.14em] text-white/70 pointer-events-none">
                  {i + 1}/{count}
                </span>

                {/* How far through this one is, and the handle for moving it.
                    The strip is taller than the line it draws so there is
                    something to catch on a phone; the line stays hairline. */}
                <span
                  className="story-scrub"
                  role="slider"
                  tabIndex={0}
                  aria-label={`Seek ${story.name}'s review`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={0}
                  onPointerDown={(event) => onScrubStart(event, story.id)}
                  onPointerMove={(event) => onScrubMove(event, story.id)}
                  onPointerUp={(event) => onScrubEnd(event, story.id)}
                  onPointerCancel={(event) => onScrubEnd(event, story.id)}
                  onKeyDown={(event) => onScrubKey(event, story.id)}
                  onClick={(event) => event.stopPropagation()}
                >
                  <span className="story-scrub-track">
                    <span
                      ref={(node) => {
                        if (node) bars.current.set(story.id, node);
                        else bars.current.delete(story.id);
                      }}
                      className="story-scrub-fill"
                    />
                    <span
                      ref={(node) => {
                        if (node) knobs.current.set(story.id, node);
                        else knobs.current.delete(story.id);
                      }}
                      className="story-scrub-knob"
                    />
                  </span>
                </span>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
};
